import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

// UI Components
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle, Loader2, Mail, ArrowLeft } from "lucide-react";
import { AlertMessage } from "@/components/ui/alert-message";
import { AuthLayout } from "@/components/layout/AuthLayout";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const { requestPasswordReset, isLoading } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!email) {
      setError("Email is required");
      return;
    }

    try {
      // Store the current value before password reset request
      const currentEmail = email;

      await requestPasswordReset(currentEmail);

      // Show success message
      setSuccessMessage(
        "If this email exists in our system, check your email for the reset link."
      );

      // Clear the form
      setEmail("");
    } catch (err: any) {
      // On error, keep the form value and show the error message
      setError(
        err.message || "Failed to request password reset. Please try again."
      );
    }
  };

  return (
    <AuthLayout
      title="Forgot Password"
      subtitle="Enter your email to receive a password reset link"
      leftContent={{
        title: "Password Recovery",
        subtitle: "& Account Access",
        description:
          "Reset your password to regain access to your account and continue using your AI-powered visual commerce analytics dashboard.",
        trustIndicator: "Secure password reset",
      }}
    >
      <AlertMessage message={error} type="error" />

      {successMessage && (
        <div className="mb-6 rounded-md border border-green-200 bg-green-50 p-4 text-sm text-green-700">
          <div className="flex items-center">
            <CheckCircle className="mr-2 h-4 w-4" />
            {successMessage}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <Mail className="h-4 w-4 text-gray-400" />
            </div>
            <Input
              id="email"
              type="email"
              placeholder="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading || !!successMessage}
              required
              className="pl-10 h-12"
            />
          </div>
        </div>

        <Button
          type="submit"
          className="w-full h-12 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-medium rounded-lg transition-colors"
          disabled={isLoading || !!successMessage}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Sending reset link...
            </>
          ) : (
            "Send Reset Link"
          )}
        </Button>
      </form>

      <div className="mt-6 text-center">
        <p className="text-gray-600">
          <Link
            to="/signin"
            className="inline-flex items-center font-medium text-purple-600 hover:text-purple-500"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to Sign In
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}
